import React, { useState, useEffect } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { object, string, array, boolean } from "yup";
import VerticalStepper from "./VerticalStepper/VerticalStepper";
import { InquiryEditValues } from "src/models/InquiryEdit.model";
import {
  useViewInquiryQuery,
  useEditInquiryMutation,
} from "src/services/InquiryServices";
import { useParams } from "react-router-dom";
import { showToast } from "src/utils/showToaster";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import { mobileRegExp } from "src/utils/validations/indianPhoneRegex";

const EditInquiryWrapper = () => {
  const { inquiryId = "" } = useParams();

  //STATE
  const [inquiryData, setInquiryData] = useState<any>();

  const { data, isLoading, isFetching } = useViewInquiryQuery(inquiryId);
  const [editInquiry] = useEditInquiryMutation();
 
  useEffect(() => {
    if (!isLoading && !isFetching) {
      setInquiryData(data?.data);
    }
  }, [data, isLoading, isFetching]);
  // Form Initial Values
  const initialValues: InquiryEditValues = {
    name: inquiryData?.name,
    mobile: inquiryData?.mobile,
    inquiryLabel:{
      label:inquiryData?.inquiryLabelName ,
      value: inquiryData?.inquiryLabelId
    }   ,
    email: inquiryData?.email,
    instagram_id: inquiryData?.instagram_id,
    whatsApp_mobile: inquiryData?.whatsApp_mobile,
    alternate_number_1: inquiryData?.alternate_number_1,
    alternate_number_2: inquiryData?.alternate_number_2,
    facebook_id: inquiryData?.facebook_id,
    youtube_id: inquiryData?.youtube_id,
    twitter_id: inquiryData?.twitter_id,
    linkedin_id: inquiryData?.linkedin_id,
    dob: inquiryData?.dob,
    marital_status: inquiryData?.marital_status,
    gender: inquiryData?.gender,

    inquiryType: {
      label: inquiryData?.inquiryType,
      value: inquiryData?.inquiryType,
    } || {},
    communication_type: inquiryData?.communication_type,
    communication_remark: inquiryData?.communication_remark,
    communication_summary: inquiryData?.communication_summary,
    inquired_for: inquiryData?.inquired_for?.[0] ? inquiryData?.inquired_for : []  ,
    interested_course: inquiryData?.interested_course?.map((el: any) => {
      return {
        course_name: {
          label: el?.courseLabel?.courseName,
          value: el?.course_name,
        },
        fees: el?.fees?.toString(),
        offline_fees: el?.courseLabel?.classroomFees?.toString(),
        online_fees: el?.courseLabel?.onlineFees?.toString(),
        Offer_Price: el?.Offer_Price,
        remark: el?.remark,
      };
    }),
    academic_history: inquiryData?.academic_history?.map((el: any) => {
      return {
        collge_school_name: el?.collge_school_name,
        course_name: el?.course_name,
        course_type: el?.course_type,
        collge_school_tier: el?.collge_school_tier,
        start_year: el?.start_year,
        end_year: el?.end_year,
        currently_pursuing: el?.currently_pursuing,
      };
    }),
    work_history: inquiryData?.work_history?.map((el: any) => {
      return {
        company_name: el?.company_name,
        job_title: el?.job_title,
        job_description: el?.job_description,
        start_year: el?.start_year,
        end_year: el?.end_year,
        currently_pursuing: el?.currently_pursuing,
      };
    }),
    after_date: inquiryData?.after_date,
    want_to_join: inquiryData?.want_to_join || "",
    want_to_join_remark: inquiryData?.want_to_join_remark || "",
    source_type: inquiryData?.source_type,
    referral_name: inquiryData?.referral_name,
    campign_name: inquiryData?.campign_name,
    campign_group: inquiryData?.campign_group,
    language: inquiryData?.language?.map((el: any) => {
      return {
        label: el?.languageName,
        value: el?._id,
      };
    }),
    timing: inquiryData?.timing?.map((el: any) => {
      return {
        label: el,
        value: el,
      };
    }),
    mode_type: inquiryData?.mode_type?.map((el: any) => {
      return {
        label: el?.modeName,
        value: el?._id,
      };
    }),
    next_followUp_required: inquiryData?.next_followUp_required,
    contact_them: inquiryData?.contact_them,
    next_followUp_date: inquiryData?.next_followUp_date,
    next_followup_time: inquiryData?.next_followup_time
      ? moment(
        inquiryData?.next_followup_time?.split("-")?.[0]?.trim(),
        "hh:mm A"
      )
      : '',
    new_batch_start: inquiryData?.new_batch_start,
    followup_batch_start_course: inquiryData?.followup_batch_start_course?.map(
      (el: any) => {
        return {
          label: el?.courseName,
          value: el?._id,
        };
      }
    ),
    next_followup_reason: inquiryData?.next_followup_reason,
    course_not_clear: inquiryData?.course_not_clear,
    countryId: {
      countryName: inquiryData?.countryLabel,
      _id: inquiryData?.countryId,
    },
    stateId: {
      stateName: inquiryData?.stateLabel,
      _id: inquiryData?.stateId,
    },
    cityId: {
      cityName: inquiryData?.cityLabel,
      _id: inquiryData?.cityId,
    },
  };

  // Validation Schema
  const validationSchema = object()
    .shape({
      name: string().required("please enter name"),
      mobile: string()
        .trim()
        .matches(mobileRegExp, "mobile number is not valid"),
      email: string().email(),
      whatsApp_mobile: string()
        .trim()
        .matches(mobileRegExp, "whatsApp mobile number is not valid"),
      alternate_number_1: string()
        .trim()
        .matches(mobileRegExp, "number is not valid"),
      alternate_number_2: string()
        .trim()
        .matches(mobileRegExp, "number is not valid"),
      instagram_id: string(),
      facebook_id: string(),
      youtube_id: string(),
      twitter_id: string(),
      linkedin_id: string(),
      dob: string(),
      marital_status: string(),
      gender: string(),
      communication_type: string(),
      communication_remark: string(),
      communication_summary: string(),
      inquired_for: array(),
      academic_history: array().of(object()),
      work_history: array().of(object()),
      want_to_join: string(),
      after_date: string(),
      want_to_join_remark: string(),
      source_type: string(),
      referral_name: string(),
      next_followUp_required: string(),
      contact_them: string(),
      next_followUp_date: string(),
      new_batch_start: boolean(),
      next_followup_reason: string(),
      course_not_clear: boolean(),
      followup_batch_start_course: array()
        .of(object())
        .test("new_batch_start", "", (value: any, context) => {
          if (context.parent?.new_batch_start && !value?.[0]?.label) {
            return context.createError({
              message: "Please select course",
              path: context.path,
            });
          }
          return true;
        }),
    })
    .test("test_contactsInfo", "", (value, context: any) => {
      const {
        mobile,
        email,
        instagram_id,
        facebook_id,
        youtube_id,
        twitter_id,
        linkedin_id,
      } = context?.options?.context;
      if (
        !(
          mobile ||
          email ||
          instagram_id ||
          facebook_id ||
          youtube_id ||
          twitter_id ||
          linkedin_id
        )
      ) {
        return context.createError({
          path: "any_one_contact",
          message: "Required",
        });
      }
    });

  // Handle Submit
  const handleSubmit = (
    values: InquiryEditValues,
    { setSubmitting, resetForm }: FormikHelpers<InquiryEditValues>
  ) => { 

    // Step 1: Remove the country code if present
    const withoutCountryCode = values?.mobile.replace(/^\+91\s?/, "");

    // Step 2: Remove all spaces
    const finalNumber = withoutCountryCode.replace(/\s/g, "");

    const formattedValues = {
      name: values?.name,
      mobile: finalNumber,
      email: values?.email,
      whatsApp_mobile: values?.whatsApp_mobile,
      alternate_number_1: values?.alternate_number_1,
      alternate_number_2: values?.alternate_number_2,
      instagram_id: values?.instagram_id,
      facebook_id: values?.facebook_id,
      youtube_id: values?.youtube_id,
      twitter_id: values?.twitter_id,
      linkedin_id: values?.linkedin_id,
      dob: values?.dob,
      marital_status: values?.marital_status,
      gender: values?.gender,
      inquiryType: values?.inquiryType.value,
      communication_type: values?.communication_type,
      communication_remark: values?.communication_remark,
      communication_summary: values?.communication_summary,
      inquired_for: values?.inquired_for,
      interested_course: values?.interested_course?.map((el: any) => {
        return {
          course_name:
            el?.course_name === ""
              ? null
              : el?.course_name?.value
          ,
          fees: el?.fees?.toString(),
          Offer_Price: el?.Offer_Price,
          remark: el?.remark,
        };
      }),
      academic_history: values?.academic_history?.map((el: any) => {
        return {
          collge_school_name: el?.collge_school_name,
          course_name: el?.course_name,
          course_type: el?.course_type,
          collge_school_tier: el?.collge_school_tier,
          start_year: el?.start_year,
          end_year: el?.end_year,
          currently_pursuing: el?.currently_pursuing,
        };
      }),
      work_history: values?.work_history?.map((el: any) => {
        return {
          company_name: el?.company_name,
          job_title: el?.job_title,
          job_description: el?.job_description,
          start_year: el?.start_year,
          end_year: el?.end_year,
          currently_pursuing: el?.currently_pursuing,
        };
      }),
      want_to_join: values?.want_to_join,
      after_date: moment(values?.after_date).format('yyyy-MM-DD'),
      want_to_join_remark: values?.want_to_join_remark,
      source_type: values?.source_type,
      referral_name: values?.referral_name,
      campign_name: values?.campign_name,
      campign_group: values?.campign_group,
      language: values?.language?.map((language: any) => language.value),
      timing: values?.timing?.map((time: any) => time.value),
      mode_type: values?.mode_type?.map((mode: any) => mode?.value),
      next_followUp_required: values?.next_followUp_required,
      contact_them: values?.contact_them,
      next_followUp_date: moment(values?.next_followUp_date).format('yyyy-MM-DD'),
      next_followup_time: moment(values?.next_followup_time).format("hh:mm A"),
      new_batch_start: values?.new_batch_start,
      followup_batch_start_course: values?.followup_batch_start_course?.map(
        (course: any) => course.value
      ),
      next_followup_reason: values?.next_followup_reason,
      course_not_clear: values?.course_not_clear,
      countryId: values?.countryId?._id,
      stateId: values?.stateId?._id,
      cityId: values?.cityId?._id,
    };
    editInquiry({
      id: inquiryId,
      body: formattedValues,
    }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <Form className="h-full  overflow-hidden">
          <div className=" h-full flex flex-col">
            {(isLoading || isFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}

            <div className="flex-1 overflow-auto pt-2">
              <VerticalStepper formikProps={formikProps} />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default EditInquiryWrapper;
